import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { resolveApiUrl } from './resolveApiUrl';
import { ApiResponseWrapper, isErrorResponse } from "@/types/api";

// Token storage utility (avoid circular imports)
function getStoredAccessToken(): string | null {
  return sessionStorage.getItem('athlyn_cms_token');
}

function getAuthMethod(): 'otp' | 'sso' | null {
  return sessionStorage.getItem('athlyn_cms_auth_method') as 'otp' | 'sso' | null;
}

function clearStoredTokens() {
  sessionStorage.removeItem('athlyn_cms_token');
  sessionStorage.removeItem('athlyn_cms_refresh');
  sessionStorage.removeItem('athlyn_cms_user');
  sessionStorage.removeItem('athlyn_cms_auth_method');
  sessionStorage.removeItem('athlyn_cms_microsoft_tokens');
}

/**
 * True only for a GENUINE session/auth failure — any 401, or a 403 that carries no business
 * `errorCode` (e.g. the bare role-check 403). A typed 403 (FORBIDDEN, PERKS_DISABLED,
 * EXTERNAL_USERS_DISABLED, NO_ACTIVE_SEAT, …) is a business refusal: it is thrown to the caller
 * and must NOT log the user out.
 */
export function isSessionAuthFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { status, errorCode } = error as { status?: number; errorCode?: unknown };
  if (status === 401) return true;
  if (status === 403) return !(typeof errorCode === 'string' && errorCode.length > 0);
  return false;
}

function authFailureError(res: Response, message: string): Error {
  const error = new Error(message);
  (error as any).status = res.status;
  (error as any).statusText = res.statusText;
  return error;
}

/**
 * Parses the standard envelope and throws a typed error for a failure. The LOGOUT decision is
 * made here — not in the React Query `retry` callbacks — so it also applies to mutations that
 * set `retry: false`. The API answers a 401 (JWT challenge) and the role-check 403 with an EMPTY
 * (or non-envelope) body; those carry no business code, so they are session failures.
 */
async function parseApiResponse<T>(res: Response): Promise<ApiResponseWrapper<T>> {
  let jsonData: ApiResponseWrapper<T>;
  try {
    jsonData = await res.json();
  } catch {
    // Empty / non-JSON body (e.g. the bare 401 challenge or role-check 403, or a proxy 413).
    if (isSessionAuthFailure({ status: res.status })) {
      clearStoredTokens();
    }
    throw authFailureError(res, 'Invalid API response: Unable to parse JSON');
  }

  // If it's an error response, throw an error but attach the full wrapper
  if (jsonData && isErrorResponse(jsonData)) {
    // Clear tokens on genuine authentication errors only — typed 403s are thrown, not logged out.
    if (isSessionAuthFailure({ status: res.status, errorCode: jsonData.error?.code })) {
      clearStoredTokens();
    }

    const errorMessage = jsonData.error?.message || res.statusText || 'An error occurred';
    const error = new Error(errorMessage);
    (error as any).status = res.status;
    (error as any).statusText = res.statusText;
    (error as any).errorCode = jsonData.error?.code;
    (error as any).errorDetails = jsonData.error?.details;
    (error as any).fullResponse = jsonData; // Attach full wrapper for detailed access
    throw error;
  }

  // A JSON body that is not our envelope (e.g. ProblemDetails) on a 401/403: no business code,
  // so it is a session failure — never hand it to the caller as data.
  if (isSessionAuthFailure({ status: res.status })) {
    clearStoredTokens();
    throw authFailureError(res, res.statusText || 'Not authorized');
  }

  return jsonData;
}

// Returns the full API response wrapper (throws on error)
export async function apiRequestWrapped<T = any>(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<ApiResponseWrapper<T>> {
  const accessToken = getStoredAccessToken();

  // Build full URL with CMS API base URL
  // Runtime config takes precedence, then build-time env; resolveApiUrl owns the last step
  // (dev default in a DEV build, a loud throw in PROD).
  const baseUrl = resolveApiUrl(
    window.APP_CONFIG?.CMS_API_BASE_URL || import.meta.env.VITE_CMS_API_BASE_URL,
    'https://cms.api.dev.athlyn.app',
    'VITE_CMS_API_BASE_URL',
  );
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

  const headers: Record<string, string> = {};
  // Multipart uploads: the browser sets the multipart Content-Type (with its boundary) itself.
  const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;

  // Add Content-Type for JSON requests with data
  if (data && !isFormData) {
    headers["Content-Type"] = "application/json";
  }
  
  // Add Authorization header if we have a token (except for auth endpoints)
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const res = await fetch(fullUrl, {
    method,
    headers,
    body: isFormData ? (data as FormData) : data ? JSON.stringify(data) : undefined,
    // credentials: "include",
  });

  return await parseApiResponse<T>(res);
}

function cmsBaseUrl(): string {
  return resolveApiUrl(
    window.APP_CONFIG?.CMS_API_BASE_URL || import.meta.env.VITE_CMS_API_BASE_URL,
    'https://cms.api.dev.athlyn.app',
    'VITE_CMS_API_BASE_URL',
  );
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* fall through to the plain filename */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
}

/**
 * Authenticated GET of a file (e.g. a CSV template). Resolves with the bytes and the server's
 * filename (Content-Disposition) or `fallbackFilename`. A failed request throws the same typed
 * error as `apiRequest` (status / errorCode), with the same logout rule.
 */
export async function apiDownload(
  url: string,
  fallbackFilename: string,
): Promise<{ blob: Blob; filename: string }> {
  const accessToken = getStoredAccessToken();
  const fullUrl = url.startsWith('http') ? url : `${cmsBaseUrl()}${url}`;
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const res = await fetch(fullUrl, { method: 'GET', headers });
  if (!res.ok) {
    // Error bodies use the standard envelope — reuse the shared parser so the error is typed.
    await parseApiResponse(res);
    const error = new Error(res.statusText || 'Download failed');
    (error as any).status = res.status;
    throw error;
  }

  const blob = await res.blob();
  const filename = filenameFromDisposition(res.headers.get('Content-Disposition')) || fallbackFilename;
  return { blob, filename };
}

// Unwraps the response data (throws on error) - for backward compatibility
export async function apiRequest<T = any>(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<T> {
  const response = await apiRequestWrapped<T>(method, url, data);

  // At this point, we know it's not an ErrorResponse (would have thrown)
  if ('pagination' in response) {
    return (response as any).data || ([] as T); // Return the data array for paginated responses, fallback to empty array
  }

  // For success responses, return the data field or fallback to the whole response if data is undefined
  const responseData = (response as any).data;
  if (responseData !== undefined) {
    return responseData;
  }

  // If data field doesn't exist, return the whole response (for backwards compatibility)
  return response as T;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const accessToken = getStoredAccessToken();
    const url = queryKey.join("/") as string;

    // Build full URL with CMS API base URL
    // resolveApiUrl owns the last step (dev default in DEV, loud throw in PROD).
    const baseUrl = resolveApiUrl(
      window.APP_CONFIG?.CMS_API_BASE_URL || import.meta.env.VITE_CMS_API_BASE_URL,
      'https://cms.api.dev.athlyn.app',
      'VITE_CMS_API_BASE_URL',
    );
    const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

    const headers: Record<string, string> = {};
    
    // Add Authorization header if we have a token
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }

    const res = await fetch(fullUrl, {
      headers,
      // credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      clearStoredTokens();
      return null;
    }

    const response = await parseApiResponse(res);

    // Unwrap the response data
    if ('pagination' in response) {
      return (response as any).data || [];
    }

    // For success responses, return the data field or fallback to the whole response if data is undefined
    const responseData = (response as any).data;
    if (responseData !== undefined) {
      return responseData;
    }

    // If data field doesn't exist, return the whole response (for backwards compatibility)
    return response;
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on 401/403. The logout itself happens in parseApiResponse (so `retry: false`
        // mutations log out too); clearing here is only a backstop for errors thrown elsewhere.
        if (error?.status === 401 || error?.status === 403) {
          if (isSessionAuthFailure(error)) clearStoredTokens();
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: (failureCount, error: any) => {
        if (error?.status === 401 || error?.status === 403) {
          if (isSessionAuthFailure(error)) clearStoredTokens();
          return false;
        }
        return failureCount < 1;
      },
    },
  },
});